/** * チェックイン詳細表示コンポーネント */
<template>
  <ifield-b-modal
    id="the-checkin-detail"
    ref="the-checkin-detail-modal"
    size="xl"
    centered
    no-body-border
    v-bind:title="$t('theCheckinDetail.checkin')"
    static
    modal-class="detail-dialog-static"
    v-on:show="$_onShowModal"
    v-on:hidden="$_onHideModal"
  >
    <b-row v-if="isContentCreated" class="dialog-body">
      <b-col cols="6" class="dialog-body-left">
        <loading-mask v-bind:isLoading="isLoading">
          <!-- チェックインユーザー -->
          <div class="dialog-user-card">
            <card-user
              v-if="data"
              can-user-name-click
              v-bind:user-id="data.user_id"
              v-bind:user-name="data.user_name"
              v-bind:profile-image-attach-file-type="
                data.profile_image ? data.profile_image.attach_file_type : null
              "
              v-bind:update-date="updateDate"
              v-bind:groups="groups"
              v-on:usercard-username-click="
                $store.dispatch('updateProfileUserId', $event)
              "
            ></card-user>
          </div>
          <div
            class="dialog-checkin-card"
            v-bind:class="{ 'dialog-checkin-card-no-action': isCheckedOut }"
          >
            <!-- チェックイン基本情報 -->
            <div class="dialog-info-title">
              <h3>
                {{ placeName }}
              </h3>
            </div>
            <!-- データリスト（チェックイン） -->
            <div>
              <data-list v-bind:data="checkinDataList" />
            </div>
            <!-- 区切り線 -->
            <div class="delimiter-line" />
            <!-- データリスト（チェックアウト） -->
            <div>
              <data-list v-bind:data="checkoutDataList" />
            </div>
          </div>
          <!-- チェックアウトボタン -->
          <div v-if="!isCheckedOut" class="dialog-checkin-card-action">
            <p v-t="'theCheckinDetail.notCheckedOut'" />
            <b-button
              v-if="canCheckout"
              size="sm"
              variant="secondary"
              v-t="'theCheckinDetail.action.checkout'"
              v-on:click="$_showCheckoutEditModal"
            />
          </div>
        </loading-mask>
      </b-col>
      <b-col cols="6" class="dialog-body-right">
        <!-- 地図 -->
        <map-control
          ref="map-control"
          v-bind="$store.getters.mapControlBinds"
          v-bind:id-prefix="mapIdPrefix"
          v-bind:height="'520px'"
          v-bind:center="center"
          v-bind:zoom="18"
          v-bind:store-sync="['mapType']"
          v-bind:control="{ header: false, layerMenu: false }"
          v-bind:can-change-indoor-level="false"
          v-bind:disabled="true"
          v-bind:layer-renderers="renderers"
          v-bind:reactive-render="isModalShown"
        />
      </b-col>
    </b-row>
    <!-- チェックアウトコメント -->
    <checkout-editor
      v-if="isContentCreated"
      ref="checkout-editor"
      v-bind:idPrefix="'edit'"
      v-bind:checkinId="checkinId"
      v-on:checkout-editend="$_onCheckoutEditEnd"
    />

    <!-- modal footer -->
    <template v-slot:modal-footer="{ cancel }">
      <!-- 閉じるボタン -->
      <b-button
        size="sm"
        variant="secondary"
        v-t="'theCheckinDetail.action.close'"
        v-on:click="cancel"
      />
    </template>
  </ifield-b-modal>
</template>

<script>
import DataList from '@/components/AppDataList.vue'
import CardUser from '@/components/AppCardUser.vue'
import CheckoutEditor from '@/views/TheCheckoutEditor.vue'
import { ATTRIBUTE_TYPE } from 'ifield-core-components'
import { RestApiClient } from 'ifield-core-components'
import { mapState } from 'vuex'
import DomainRenderer from '@/js/map-renderer/DomainRenderer.js'
import { toLocalTz } from '@/js/TimezoneHelper'
import { useHandleApiError } from '@/composables/UseHandleApiError.js'

// レイヤーID
const LAYER_ID = 'checkin'

// レンダラーファクトリ
const rendererFactory = new DomainRenderer({ layerId: LAYER_ID })

export default {
  name: 'TheCheckinDetail',
  components: {
    'data-list': DataList,
    'card-user': CardUser,
    'checkout-editor': CheckoutEditor,
  },
  filters: {
    convertToDate: function (value) {
      if (value) {
        return new Date(value)
      }
      return null
    },
  },
  setup(props, context) {
    const { handleApiError } = useHandleApiError(props, context)
    return { handleApiError }
  },
  data: function () {
    return {
      /**
       * 表示対象のレポートID
       * @type {number}
       */
      checkinId: null,
      /**
       * チェックイン詳細データ
       *
       * API(/ifield/users/api/checkin/:id)のレスポンス
       * @type {Object}
       */
      data: null,
      /**
       * ロード中の場合はtrue
       * @type {Boolean}
       */
      isLoading: false,
      /**
       * 自レイヤーのレンダラー
       * @type {Object}
       */
      myRenderer: null,
      /**
       * チェックアウトデータを更新した場合はtrue
       * @type {boolean}
       */
      isUpdated: false,
      /**
       * ダイアログの中身を生成済みの場合はtrue
       *
       * 地図を作り直さないため、一度開いたら閉じても保持する
       * @type {Boolean}
       */
      isContentCreated: false,
      /**
       * ダイアログを表示中の場合はtrue
       *
       * 非表示の間は地図のリアクティブな描画を止める
       * @type {Boolean}
       */
      isModalShown: false,
    }
  },
  computed: {
    /**
     * 地図要素のIDプレフィックス
     *
     * 同じ画面に本コンポーネントが複数載るため、インスタンスごとに一意にする
     * @type {String}
     */
    mapIdPrefix: function () {
      return `checkin-detail-${this._uid}-`
    },
    ...mapState('checkinMarker', ['iconAtlas', 'iconMapping']),
    /**
     * 報告ユーザーの所属グループ
     * @type {Array.<Object>}
     */
    groups: function () {
      if (!this.data) return []

      return (
        this.$store.getters['user/getUserById'](this.data.user_id)?.groups || []
      )
    },
    /**
     * 地図binding用render関数配列
     * @type {Array.<Function>}
     */
    renderers: function () {
      const renderers = [...this.$store.getters['map/layerRenderers']]
      if (this.myRenderer) {
        renderers.push(this.myRenderer.render)
      }
      return renderers
    },
    /**
     * 中心座標
     * @type {Object}
     */
    center: function () {
      if (
        this.data &&
        this.data.geom &&
        this.data.geom.geometry &&
        this.data.geom.geometry.coordinates
      ) {
        const [lng, lat] = this.data.geom.geometry.coordinates
        return { lat, lng }
      } else {
        return this.$store.getters['map/center']
      }
    },
    /**
     * チェックイン先のプレイス名
     * @type {String}
     */
    placeName: function () {
      if (this.data) {
        return this.data.place_name
      } else {
        return ''
      }
    },
    /**
     * 更新日時
     * @type {Date}
     */
    updateDate: function () {
      return this.data?.update_at ? new Date(this.data?.update_at) : null
    },
    /**
     * チェックインのデータリストコンポーネント用のデータ
     * @type {Object}
     */
    checkinDataList: function () {
      if (!this.data) {
        return null
      }

      return {
        standard: [
          // チェックイン日時
          {
            label: this.$t('theCheckinDetail.label.checkinDate'),
            type: ATTRIBUTE_TYPE.DATETIME,
            value: this.data.checkin_update_at,
          },
          // チェックインコメント
          {
            label: this.$t('theCheckinDetail.label.comment'),
            type: ATTRIBUTE_TYPE.STRING,
            value: this.data.comment,
          },
        ],
      }
    },
    /**
     * チェックアウトのデータリストコンポーネント用のデータ
     * @type {Object}
     */
    checkoutDataList: function () {
      if (!this.data) {
        return null
      }

      return {
        standard: [
          // チェックアウト日時
          {
            label: this.$t('theCheckinDetail.label.checkoutDate'),
            type: ATTRIBUTE_TYPE.DATETIME,
            value: this.data.checkout_at,
          },
          // チェックアウトコメント
          {
            label: this.$t('theCheckinDetail.label.comment'),
            type: ATTRIBUTE_TYPE.STRING,
            value: this.data.checkout_comment,
          },
        ],
      }
    },
    /**
     * チェックアウト済の場合はtrue
     * @type {Boolean}
     */
    isCheckedOut: function () {
      // チェックアウト済み確認
      return !this.data || this.data.checkout_at
    },
    /**
     * チェックアウト可能な場合はtrue
     * @type {Boolean}
     */
    canCheckout: function () {
      // ユーザーに承認権限がある場合又は自分のチェックインのみtrue
      return (
        this.$store.state.login.isManager ||
        this.data.user_id === this.$store.state.login.userId
      )
    },
  },
  watch: {
    data: function () {
      this.$_updateCheckinLayer()
    },
  },
  mounted: function () {
    this.$log.info('theCheckinDetail mounted')
  },
  methods: {
    /**
     * モーダルダイアログを表示する
     * @param {number} checkinId チェックインID
     */
    showModal: function (checkinId) {
      this.checkinId = checkinId
      this.$_showCheckin()
      this.$refs['the-checkin-detail-modal'].show()
    },

    /**
     * モーダルダイアログを非表示にする
     */
    hideModal: function () {
      this.$refs['the-checkin-detail-modal'].hide()
    },

    /**
     * チェックイン情報を表示する
     */
    $_showCheckin: async function () {
      if (!this.checkinId) {
        return
      }

      this.$_showLoading()
      // URL作成
      var url = RestApiClient.createApiUrl('checkin/' + this.checkinId)
      try {
        const d = await RestApiClient.executeGetApi(url).then(
          ({ data }) => data
        )
        this.data = {
          ...d,
          checkin_update_at: toLocalTz(d.checkin_update_at),
          checkout_at: toLocalTz(d.checkout_at),
          update_at: toLocalTz(d.update_at),
        }
      } catch (error) {
        this.handleApiError(error)
        this.$_hideLoading()
        return
      }

      this.$_hideLoading()
    },

    /**
     * チェックインレイヤー更新
     */
    $_updateCheckinLayer: function () {
      // データが空のときはレイヤーを作成しない
      if (!this.data) {
        this.myRenderer = null
        return
      }

      const props = {
        iconAtlas: this.iconAtlas,
        iconMapping: this.iconMapping,
      }
      this.myRenderer = rendererFactory.createRenderer([this.data], props)
    },

    /**
     * チェックアウト編集用モーダルを表示
     */
    $_showCheckoutEditModal: function () {
      this.$refs['checkout-editor'].showModal()
    },

    /**
     * チェックアウトコメント編集終了時の処理
     */
    $_onCheckoutEditEnd: function () {
      this.isUpdated = true
      this.$_showCheckin()
    },

    /**
     * モーダルダイアログを表示する時の処理
     */
    $_onShowModal: function () {
      // 地図を作り直さないよう、一度生成した中身は閉じても保持する
      this.isContentCreated = true
      this.isModalShown = true
    },

    /**
     * モーダルダイアログ非表示時の処理
     */
    $_onHideModal: function () {
      this.isModalShown = false
      /**
       * チェックアウトコメントモーダルを閉じるイベント
       * @param {Boolean} isUpdated 内容が更新されている場合はtrue
       */
      this.$emit('hide-modal', this.isUpdated)
      // クリア処理
      this.$_clear()
    },

    /**
     * クリア処理
     */
    $_clear: function () {
      this.checkinId = null
      this.data = null
      this.isUpdated = false
    },

    /**
     * ローディングを表示する
     */
    $_showLoading: function () {
      this.isLoading = true
    },

    /**
     * ローディングを非表示にする
     */
    $_hideLoading: function () {
      this.isLoading = false
    },
  },
}
</script>

<style lang="scss" scoped>
.dialog-body {
  background-color: #ffffff;
  display: flex;
  height: 520px;
  margin: 0;
  width: 100%;
}

.dialog-body-left {
  padding-left: 0;
  height: 100%;
}

.dialog-user-card {
  width: 100%;
  padding: 10px;
  box-sizing: border-box;
  background-color: #ffffff;
  border: 1px solid #f0f0f0;
}

.dialog-checkin-card {
  background-color: #f0f0f0;
  border: 1px solid #f0f0f0;
  height: calc(100% - 113px);
  overflow-x: hidden;
  overflow-y: auto;
  padding: 20px;
  text-align: left;
  @include mixin-scrollbar;

  ::v-deep .dialog-checkin-card {
    padding: 0;
  }

  ::v-deep .data-list {
    .data-title {
      width: 100px;
    }
  }
}

/* 下部にチェックアウトボタンがない場合の高さ指定 */
.dialog-checkin-card-no-action {
  height: calc(100% - 62px);
}

.dialog-checkin-card-action {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 51px;
  padding: 10px;
  box-sizing: border-box;
  border: 1px solid #f0f0f0;
  background-color: #ffffff;

  p {
    margin: 0;
    font-size: 0.8em;
    word-break: normal;
  }
}

.dialog-info-title {
  margin: 0 0 20px;

  h3 {
    font-size: 1em;
    font-weight: bold;
    margin: 0 0 10px;
  }
}

.dialog-body-right {
  padding-right: 0;
}

.delimiter-line {
  width: 100%;
  position: relative;
  top: 0;
  left: 0;
  display: flex;
  margin-top: 10px;
  margin-bottom: 16px;

  &::before {
    content: '';
    width: 100%;
    height: 2px;
    background-color: #e0e0e0;
    position: absolute;
    top: 50%;
    left: 0;
  }
}

::v-deep .list-wrapper {
  margin: 0 !important;
  li {
    margin-bottom: 4px !important;
  }
}
</style>
